import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import { Icon, Theme, Spacer, Input } from "@dspworkplace/ui";
import API from "./api";
import { useAuth } from "../../Auth";

const DropdownContainer = styled.div`
    position: relative;
    display: inline-block;
`;

const DropdownButton = styled.button`
    background: ${(props) => (props.hasSelection ? "transparent" : Theme.colors.info.bg)};
    border: none;
    padding: ${(props) => (props.hasSelection ? "4px 8px" : "8px")};
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${Theme.colors.info.text};
    font-size: ${Theme.font.small.size};

    &:hover {
        background: ${(props) => (props.hasSelection ? Theme.colors.neutrals.silver : Theme.colors.info.border)};
    }
`;

const SelectedText = styled.span`
    text-decoration: underline;
    color: ${Theme.colors.info.text};
`;

const DropdownPanel = styled.div`
    position: absolute;
    top: 100%;
    left: ${(props) => (props.leftRightPosition === "right" ? "auto" : "0")};
    right: ${(props) => (props.leftRightPosition === "right" ? "0" : "auto")};
    width: 600px;
    max-height: 400px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    border: 1px solid ${Theme.colors.neutrals.silver};
    z-index: 1000;
    overflow: hidden;
`;

const SearchContainer = styled.div`
    padding: 12px;
    border-bottom: 1px solid ${Theme.colors.neutrals.silver};
`;

const TableContainer = styled.div`
    max-height: 320px;
    overflow-y: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const TableHeader = styled.th`
    text-align: left;
    padding: 10px 12px;
    background: ${Theme.colors.neutrals.white};
    font-size: ${Theme.font.extraSmall.size};
    font-weight: 600;
    color: ${Theme.colors.neutrals.gray};
    cursor: pointer;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 1;

    &:hover {
        background: ${Theme.colors.neutrals.silver};
    }
`;

const TableRow = styled.tr`
    border-bottom: 1px solid ${Theme.colors.neutrals.lightGray};
    cursor: pointer;
    background: ${(props) => (props.highlighted ? Theme.colors.info.bg : "transparent")};

    &:hover {
        background: ${Theme.colors.info.bg};
    }
`;

const TableCell = styled.td`
    padding: 10px 12px;
    font-size: ${Theme.font.small.size};
    white-space: nowrap;
`;

const ShiftBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const ShiftColorDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => props.color || Theme.colors.neutrals.gray};
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999;
`;

const ClearButton = styled.span`
    cursor: pointer;
    color: ${Theme.colors.neutrals.gray};

    &:hover {
        color: ${Theme.colors.error.text};
    }
`;

// const formatWaveTime = (time) => {
//     if (!time) return '';
//     const [hours, minutes] = time.split(':');
//     const h = parseInt(hours, 10);
//     const ampm = h >= 12 ? 'PM' : 'AM';
//     const hour12 = h % 12 || 12;
//     return `${hour12}:${minutes} ${ampm}`;
// };

const VehicleSelectorEnhanced = ({ driver, addChange, pageLoadOut, isEnable = true }) => {
    const { api } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("asc");
    const [selectedVehicle, setSelectedVehicle] = useState(driver.vehicle?.id ? driver.vehicle : null);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [keyCounter, setKeyCounter] = useState(0);
    const searchInputRef = useRef(null);
    const [position, setPosition] = useState(null);
    const dropdownRef = useRef(null);
    const loadVehicles = useCallback(async () => {
        setLoading(true);
        try {
            const data = await API.fetchVehiclesDataEnhanced(
                {
                    date: driver.shiftDate,
                    driver: driver.driverId,
                    id: driver.id,
                    station: driver.stationId,
                },
                api
            );
            setVehicles(data || []);
        } catch (error) {
            console.error("Error loading vehicles:", error);
        }
        setLoading(false);
    }, [driver, api]);

    // Sort and filter vehicles
    const sortedAndFilteredVehicles = useMemo(() => {
        let filtered = vehicles.filter((vehicle) => {
            const searchLower = searchTerm.toLowerCase();
            const unitMatch = vehicle.vehicleId?.toLowerCase().includes(searchLower);
            const shiftMatch = vehicle.shiftName?.toLowerCase().includes(searchLower);
            const assignmentMatch = vehicle.driverName?.toLowerCase().includes(searchLower);
            return unitMatch || assignmentMatch || shiftMatch;
        });

        // Default sort: unassigned first, then wave time, then shift type, then vehicle name
        filtered.sort((a, b) => {
            // Priority 0: Vehicle Status Active 
            if (a.vehicleStatus === "Active" && b.vehicleStatus !== "Active") {
                return -1;
            }
            if (a.vehicleStatus !== "Active" && b.vehicleStatus === "Active") {
                return 1;
            }
            // Priority 1: Not assigned first
            const aUnassigned = !a.driverName ? 0 : 1;
            const bUnassigned = !b.driverName ? 0 : 1;
            if (aUnassigned !== bUnassigned) return aUnassigned - bUnassigned;

            // Priority 2: Earlier wave time
            const aWave = a.waveTime || "";
            const bWave = b.waveTime || "";
            if (aWave !== bWave) return aWave.localeCompare(bWave);

            // Priority 3: Shift type alphabetically
            const aShift = a.shiftName || "";
            const bShift = b.shiftName || "";
            if (aShift !== bShift) return aShift.localeCompare(bShift);

            // Priority 4: Vehicle name alphanumerically
            return a.vehicleId.localeCompare(b.vehicleId, undefined);
        });

        // Apply column sort if selected
        if (sortColumn) {
            filtered.sort((a, b) => {
                let aVal, bVal;

                if (sortColumn === "vehicleId") {
                    aVal = a.vehicleId || "";
                    bVal = b.vehicleId || "";
                } else if (sortColumn === "driverName") {
                    aVal = a.driverName || "";
                    bVal = b.driverName || "";
                } else if (sortColumn === "shiftName") {
                    aVal = a.shiftName || "";
                    bVal = b.shiftName || "";
                } else if (sortColumn === "waveTime") {
                    aVal = a.waveTime || "";
                    bVal = b.waveTime || "";
                } else if (sortColumn === "vehicleStatus") {
                    aVal = a.vehicleStatus || "";
                    bVal = b.vehicleStatus || "";
                }

                const comparison = aVal.localeCompare(bVal, undefined, { numeric: true });
                return sortDirection === "asc" ? comparison : -comparison;
            });
        }
        setKeyCounter(keyCounter + 1);
        return filtered;
    }, [vehicles, searchTerm, sortColumn, sortDirection]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen) {
            loadVehicles();
            // setTimeout(() => {
            //     if (searchInputRef.current) {
            //         searchInputRef.current.focus();
            //     }
            // }, 100);
            setHighlightedIndex(0);
            // Fix the popup route code
            const rect = dropdownRef.current?.getBoundingClientRect();
            if (rect) {
                const panelWidth = 600;
                const spaceRight = window.innerWidth - rect.left;
                setPosition(spaceRight >= panelWidth + 20 ? "left" : "right");
            }
        }
    }, [isOpen, loadVehicles]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;

            if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightedIndex((prev) => (prev < sortedAndFilteredVehicles.length - 1 ? prev + 1 : prev));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
            } else if (e.key === "Enter" && sortedAndFilteredVehicles.length > 0) {
                e.preventDefault();
                handleSelectVehicle(sortedAndFilteredVehicles[highlightedIndex]);
            } else if (e.key === "Escape") {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            window.addEventListener("keydown", handleKeyDown);
            return () => window.removeEventListener("keydown", handleKeyDown);
        }
    }, [isOpen, highlightedIndex, sortedAndFilteredVehicles]);

    const handleSelectVehicle = (vehicle) => {
        setSelectedVehicle(vehicle);
        setIsOpen(false);

        // Call the addChange callback to save the selection
        addChange({
            description: "vehicle",
            driverId: driver.id,
            callback: () => {
                driver.vehicle = {
                    id: vehicle.id,
                    unit: vehicle.vehicleId,
                };
                // Call API to save the vehicle assignment
                callAddVehicleApi(vehicle.id, driver.id, driver.name);
            },
        });
    };

    const callAddVehicleApi = async (vehicleId, routeId, name) => {
        const paramsVehicleAddJson = {
            actions: {
                response: {
                    TempDriverRoute: {
                        custom: {
                            functionName: "addLoadOutTempDriverRouteData",
                            get: "result",
                            excludes: [
                                "skill",
                                "vehicle",
                                "device",
                                "driver",
                                "station",
                                "shiftType",
                                "schedule",
                                "status",
                                "routeId",
                                "createdBy",
                                "kickoffLog",
                                "vehicleDriverRecord",
                                "oldDriver",
                                "oldDriverRoute",
                            ],
                            criteria: {
                                vehicleId: vehicleId,
                                routeId: routeId,
                                name: name,
                                type: "vehicle",
                            },
                        },
                    },
                },
            },
        };
        try {
            await api.post("/api/lazy/manage/data", paramsVehicleAddJson);
        } catch (error) {
            console.error("Error saving vehicle:", error);
        }
    };

    const handleClearSelection = (e) => {
        e.stopPropagation();
        setSelectedVehicle(null);
        callAddVehicleApi(null, driver.id, driver.name);
        addChange({
            description: "vehicle",
            driverId: driver.id,
            callback: () => {
                driver.vehicle = {};
            },
        });
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    // const isLoadoutCompleted = driver.isLoadoutCompleted === true || driver.isLoadoutCompleted === "LOAD_OUT_COMPLETED";
    // const isRTSCompleted = driver.isRTSCompleted === true || driver.isRTSCompleted === "RTS_COMPLETED";
    const enabled = driver.isRestore !== true && isEnable && ![8,9,10].includes(parseInt(driver?.shiftType?.category));

    if (!enabled) {
        return <span>{selectedVehicle?.vehicleId || driver.vehicle?.unit || "–"}</span>;
    }

    return (
        <DropdownContainer ref={dropdownRef}>
            <DropdownButton
                hasSelection={!!selectedVehicle?.vehicleId || !!driver.vehicle?.unit}
                onClick={() => setIsOpen(!isOpen)}
            >
                {selectedVehicle?.vehicleId || driver.vehicle?.unit ? (
                    <>
                        <SelectedText>{selectedVehicle?.vehicleId || driver.vehicle?.unit}</SelectedText>
                        <Icon.Edit size="14px" />
                        <ClearButton onClick={handleClearSelection}>
                            <Icon.Times size="14px" />
                        </ClearButton>
                    </>
                ) : (
                    <Icon.Plus size="16px" />
                )}
            </DropdownButton>

            {isOpen && (
                <>
                    <Overlay onClick={() => setIsOpen(false)} />
                    <DropdownPanel leftRightPosition={position}>
                        <SearchContainer>
                            <Input
                                ref={searchInputRef}
                                placeholder="Search vehicles..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                size="big"
                            />
                        </SearchContainer>

                        <TableContainer>
                            {loading ? (
                                <Spacer all={3} style={{ textAlign: "center" }}>
                                    Loading...
                                </Spacer>
                            ) : (
                                <Table>
                                    <thead>
                                        <tr>
                                            <TableHeader onClick={() => handleSort("vehicleId")}>
                                                Vehicle{" "}
                                                {sortColumn === "vehicleId" && (sortDirection === "asc" ? "↑" : "↓")}
                                            </TableHeader>
                                            <TableHeader onClick={() => handleSort("driverName")}>
                                                Currently Assigned{" "}
                                                {sortColumn === "driverName" && (sortDirection === "asc" ? "↑" : "↓")}
                                            </TableHeader>
                                            <TableHeader onClick={() => handleSort("vehicleStatus")}>
                                                Vehicle Status{" "}
                                                {sortColumn === "vehicleStatus" && (sortDirection === "asc" ? "↑" : "↓")}
                                            </TableHeader>
                                            <TableHeader onClick={() => handleSort("shiftName")}>
                                                Shift Type{" "}
                                                {sortColumn === "shiftName" && (sortDirection === "asc" ? "↑" : "↓")}
                                            </TableHeader>
                                            <TableHeader onClick={() => handleSort("waveTime")}>
                                                Wave Time{" "}
                                                {sortColumn === "waveTime" && (sortDirection === "asc" ? "↑" : "↓")}
                                            </TableHeader>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedAndFilteredVehicles.map((vehicle, index) => (
                                            <TableRow
                                                key={index + "-" + keyCounter}
                                                highlighted={index === highlightedIndex}
                                                onClick={() => handleSelectVehicle(vehicle)}
                                            >
                                                <TableCell>{vehicle.vehicleId}</TableCell>
                                                <TableCell>{vehicle.driverName || "–"}</TableCell>
                                                <TableCell>{vehicle.vehicleStatus || "–"}</TableCell>
                                                <TableCell>
                                                    {vehicle?.shiftName ? (
                                                        <ShiftBadge>
                                                            <ShiftColorDot color={vehicle.shiftColor} />
                                                            {vehicle.shiftName}
                                                        </ShiftBadge>
                                                    ) : (
                                                        "–"
                                                    )}
                                                </TableCell>
                                                <TableCell>{vehicle.waveTime || "–"}</TableCell>
                                            </TableRow>
                                        ))}
                                        {sortedAndFilteredVehicles.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} style={{ textAlign: "center" }}>
                                                    No vehicles found
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </TableContainer>
                    </DropdownPanel>
                </>
            )}
        </DropdownContainer>
    );
};

export default VehicleSelectorEnhanced;
